import React from "react";
import { ToggleButton } from "@mui/material";
import { NavBarContainer, StyledToggleButtonGroup } from "./MobileNavBarStyle";
import { RiskViewerKind, setWilmaState, useRiskViewerState } from "../../features/riskViewer/riskViewerSlice";
import {
    DrawerStateKinds,
    openMultistartSelection,
    openTripLibrary,
    openTripResults,
    useDrawerState
} from "../../features/Drawer/drawerSlice";
import { faMap, faRoute, faRv } from "@fortawesome/sharp-duotone-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch } from "react-redux";
import { NAVBAR_LIBRARY_TEXT, NAVBAR_ROUTE_TEXT, NAVBAR_WILMA_TEXT } from "../UserVisibleStrings";
import { setSelectedTime } from "../../features/surface/surfaceSlice";
import { RouteRequestState } from "../../generated/gpb/api/route";

export interface MobileNavBarProps
{
    /**
     * Function to handle closing the trip drawer.
     */
    onClose: () => void;
}

/**
 * A mobile navigation bar component with toggle buttons.
 *
 * @param {MobileNavBarProps} props - The props for the MobileNavBar component.
 * @returns {JSX.Element} The rendered MobileNavBar component.
 */
const MobileNavBar: React.FC<MobileNavBarProps> = ( { onClose } ) =>
{
    const riskViewerState = useRiskViewerState();
    const drawerState = useDrawerState();
    const dispatch = useDispatch();

    /**
     * Handles the change of the selected toggle button.
     *
     * @param {React.MouseEvent<HTMLElement>} event - The mouse event.
     * @param {string | null} newOption - The new selected option.
     */
    const handleToggleChange = (
        event: React.MouseEvent<HTMLElement>,
        newOption: string | null
    ) =>
    {
        if ( newOption !== null )
        {
            if ( newOption === DrawerStateKinds.Closed )
            {
                if ( riskViewerState.kind !== RiskViewerKind.WILMA_STATE )
                {
                    dispatch( setWilmaState() );
                    dispatch( setSelectedTime( Date.now() ) );
                }
                onClose();
            }
            else if ( newOption === DrawerStateKinds.TripLibrary )
            {
                dispatch( openTripLibrary() );
            }
            else if ( newOption === DrawerStateKinds.TripResults )
            {
                if (
                    riskViewerState.kind === RiskViewerKind.TRIP_STATE &&
                    riskViewerState.selectedRoute !== undefined &&
                    riskViewerState.tripState !== undefined
                )
                {
                    if ( riskViewerState.tripState.routeState === RouteRequestState.SHOW_OPTIONS )
                    {
                        dispatch(
                            openMultistartSelection( {
                                trip: riskViewerState.tripState,
                                routeId: riskViewerState.selectedRoute
                            } )
                        );
                    }
                    else
                    {
                        dispatch(
                            openTripResults( {
                                trip: riskViewerState.tripState,
                                routeId: riskViewerState.selectedRoute
                            } )
                        );
                    }
                }
            }
        }
    };

    return (
        <NavBarContainer>
            <StyledToggleButtonGroup
                value = {drawerState.kind}
                color = "primary"
                exclusive
                onChange = {handleToggleChange}
                aria-label = "toggle button group"
            >
                <ToggleButton value = {DrawerStateKinds.Closed} aria-label = "map">
                    <FontAwesomeIcon icon = {faMap} size = "lg" fixedWidth/>
                    {NAVBAR_WILMA_TEXT}
                </ToggleButton>
                <ToggleButton value = {DrawerStateKinds.TripLibrary} aria-label = "library">
                    <FontAwesomeIcon icon = {faRv} size = "lg" fixedWidth/>
                    {NAVBAR_LIBRARY_TEXT}
                </ToggleButton>
                {drawerState.kind !== DrawerStateKinds.MultistartSelection &&
                    <ToggleButton
                        value = {DrawerStateKinds.TripResults}
                        aria-label = "metrics"
                        disabled = {riskViewerState.kind !== RiskViewerKind.TRIP_STATE}
                    >
                        <FontAwesomeIcon icon = {faRoute} size = "lg" fixedWidth/>
                        {NAVBAR_ROUTE_TEXT}
                    </ToggleButton>}
                {/*Because the route button now has overloaded functionality (multistart drawer or overview drawer)
                 we have two separate values which should show it as selected. The value prop of the toggle button
                 must be single valued. Thus, we conditioanlly render this button only when the drawer state is
                 MultistartSelection in order to show "Route" as highlighted. */}
                {drawerState.kind === DrawerStateKinds.MultistartSelection &&
                    <ToggleButton
                        value = {DrawerStateKinds.MultistartSelection}
                        aria-label = "metrics"
                        disabled = {riskViewerState.kind !== RiskViewerKind.TRIP_STATE}
                    >
                        <FontAwesomeIcon icon = {faRoute} size = "lg" fixedWidth/>
                        {NAVBAR_ROUTE_TEXT}
                    </ToggleButton>}
            </StyledToggleButtonGroup>
        </NavBarContainer>
    );
};

export default MobileNavBar;
